// 플랫폼별 타임스탬프 URL 생성
export const getVideoUrl = (baseUrl, seconds) => {
  if (!baseUrl) return null;
  const t = Math.floor(seconds);
  try {
    const url = new URL(baseUrl);
    if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
      url.searchParams.set('t', t);
      return url.toString();
    }
    if (url.hostname.includes('weverse.io')) {
      url.searchParams.set('begin', t);
      return url.toString();
    }
  } catch (e) {}
  return baseUrl;
};

// fromm은 fromm.my 짧은 링크와 app.frommyarti.com 딥링크(내부에 channel.frommyarti.com 포함) 두 형식이 섞여 있음
export const getVideoPlatform = (url) => {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube';
  if (url.includes('weverse.io')) return 'Weverse';
  if (url.includes('fromm.my') || url.includes('frommyarti.com')) return 'fromm';
  return null;
};

// 선택된 대사들을 줄바꿈으로 연결
export const joinSnippets = (snippets) => snippets.join('\n');

export const cleanText = (s) => (typeof s === 'string' ? s.replace(/\s+/g, '').toLowerCase() : '');

export const getTitleDate = (title) => {
  const match = title?.match(/(\d{6})/);
  return match ? parseInt(match[1], 10) : 0;
};

export const MATCH = Object.freeze({ EXACT: 3, SYNONYM: 2, FUZZY: 1, NONE: 0 });

// 첫 요소는 항상 원본 토큰 — matchTier가 직접 일치와 동의어 일치를 구분하는 데 의존
export const expandToken = (token, synonyms) => {
  const set = new Set([token]);
  Object.entries(synonyms).forEach(([key, list]) => {
    if (token.includes(key)) list.forEach(s => set.add(s));
    if (list.some(s => token.includes(s))) {
      set.add(key);
      list.forEach(s => set.add(s));
    }
  });
  return [...set];
};

const HANGUL_START = 0xAC00;
const HANGUL_END = 0xD7A3;
const JONGSEONG_COUNT = 28;
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 같은 초성·중성의 음절 28개(해·핵·…·행·…·핳)는 유니코드상 연속이라 [해-핳] 한 범위로 받침 변형을 전부 잡는다
export const fuzzyPattern = (token) => {
  if (token.length < 2) return null;
  const code = token.charCodeAt(token.length - 1);
  if (code < HANGUL_START || code > HANGUL_END) return null;
  const base = code - ((code - HANGUL_START) % JONGSEONG_COUNT);
  const range = `[${String.fromCharCode(base)}-${String.fromCharCode(base + JONGSEONG_COUNT - 1)}]`;
  return new RegExp(escapeRegExp(token.slice(0, -1)) + range);
};

export const matchTier = (clean, keywords, fuzzy) => {
  if (clean.includes(keywords[0])) return MATCH.EXACT;
  for (let i = 1; i < keywords.length; i++) {
    if (clean.includes(keywords[i])) return MATCH.SYNONYM;
  }
  if (fuzzy && fuzzy.test(clean)) return MATCH.FUZZY;
  return MATCH.NONE;
};

const prepareTokens = (tokens, synonyms, fuzzy) => ({
  keywords: tokens.map(t => expandToken(t, synonyms)),
  patterns: tokens.map(t => (fuzzy ? fuzzyPattern(t) : null)),
});

// 영상 단위 AND 검색. tier는 토큰별 최고 등급의 최솟값 — 가장 약하게 맞은 토큰이 영상 등급을 결정
export const searchVideos = (videos, tokens, synonyms, { fuzzy = false } = {}) => {
  if (tokens.length === 0) return [];
  const { keywords, patterns } = prepareTokens(tokens, synonyms, fuzzy);
  const out = [];
  for (const video of videos) {
    if (!Array.isArray(video.content)) continue;
    const best = new Array(tokens.length).fill(MATCH.NONE);
    const hits = [];
    for (const line of video.content) {
      if (!line) continue;
      const clean = line.clean ?? cleanText(line.text);
      if (!clean) continue;
      let lineTier = MATCH.NONE;
      for (let t = 0; t < tokens.length; t++) {
        const tier = matchTier(clean, keywords[t], patterns[t]);
        if (tier > best[t]) best[t] = tier;
        if (tier > lineTier) lineTier = tier;
      }
      if (lineTier) hits.push({ line, tier: lineTier });
    }
    const tier = Math.min(...best);
    if (tier) out.push({ video, hits, tier });
  }
  return out;
};

// 메시지 단위 AND 검색
export const searchDms = (dms, tokens, synonyms, { fuzzy = false } = {}) => {
  if (tokens.length === 0) return [];
  const { keywords, patterns } = prepareTokens(tokens, synonyms, fuzzy);
  const out = [];
  for (const msg of dms) {
    const clean = msg.clean ?? cleanText(msg.content);
    if (!clean) continue;
    let tier = MATCH.EXACT;
    for (let t = 0; t < tokens.length && tier; t++) {
      tier = Math.min(tier, matchTier(clean, keywords[t], patterns[t]));
    }
    if (tier) out.push({ msg, tier });
  }
  return out;
};

// 일치 등급 우선, 같은 등급 안에서는 날짜순 (multiplier: -1 최신순, 1 과거순)
export const sortResults = (arr, multiplier) =>
  [...arr].sort((a, b) =>
    ((b.score ?? MATCH.EXACT) - (a.score ?? MATCH.EXACT)) ||
    (getTitleDate(a.metadata.video) - getTitleDate(b.metadata.video)) * multiplier
  );

// 최고 점수에서 gap 이상 떨어진 결과는 제외. 상위 minKeep개는 항상 유지
export const rerankImageMatches = (matches, { gap = 0.15, minKeep = 3 } = {}) => {
  const sorted = [...matches].sort((a, b) => b.score - a.score);
  if (sorted.length === 0) return sorted;
  const floor = sorted[0].score - gap;
  return sorted.filter((m, i) => i < minKeep || m.score >= floor);
};

// 하이라이트용 매처: 토큰+동의어 전체와 (옵션) 받침 변형 패턴
export const buildHighlighter = (tokens, synonyms, fuzzy) => ({
  keywords: [...new Set(tokens.flatMap(t => expandToken(t, synonyms)))],
  patterns: fuzzy ? tokens.map(fuzzyPattern).filter(Boolean) : [],
});

// 원문을 일치/비일치 세그먼트로 분리. 검색과 같은 기준(공백 무시·소문자)으로 찾은 뒤 원문 인덱스로 되돌린다
export const highlightSegments = (text, matcher) => {
  if (!text) return [];
  const cleanChars = [];
  const origIndex = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/\s/.test(ch)) continue;
    for (const lc of ch.toLowerCase()) {
      cleanChars.push(lc);
      origIndex.push(i);
    }
  }
  const clean = cleanChars.join('');
  const hit = new Array(text.length).fill(false);
  // 원문 범위로 칠하면 사이에 낀 공백도 함께 칠해져 한 덩어리로 보인다
  const markRange = (start, end) => {
    if (end <= start) return;
    for (let i = origIndex[start]; i <= origIndex[end - 1]; i++) hit[i] = true;
  };
  for (const kw of matcher.keywords) {
    if (!kw) continue;
    let idx = clean.indexOf(kw);
    while (idx !== -1) {
      markRange(idx, idx + kw.length);
      idx = clean.indexOf(kw, idx + 1);
    }
  }
  for (const p of matcher.patterns) {
    const re = new RegExp(p.source, 'g');
    let m;
    while ((m = re.exec(clean)) !== null) {
      markRange(m.index, m.index + m[0].length);
      if (m[0].length === 0) re.lastIndex++;
    }
  }
  const segments = [];
  for (let i = 0; i < text.length; i++) {
    const last = segments[segments.length - 1];
    if (last && last.hit === hit[i]) last.text += text[i];
    else segments.push({ text: text[i], hit: hit[i] });
  }
  return segments;
};
